import type { LandingRouletteSpinsResponse } from '@/lib/landing-roulette'

export const LANDING_PROFILE_SYSTEM_ID = 'landing-profile-system'
export const LANDING_PROFILE_ACHIEVEMENTS_ID = 'campaign-achievements'
export const LANDING_PROFILE_RANKING_HEADING_ID = 'top-donors-heading'

type LandingProfileDockTabId = 'numeros' | 'roleta' | 'cadastro'

export type LandingProfileDockTone =
  | 'home'
  | 'numbers'
  | 'roulette'
  | 'scratch'
  | 'info'
  | 'profile'
  | 'shop'
  | 'ai'

export type LandingProfileDockItem = {
  id: string
  label: string
  accessibleName: string
  icon: string
  tone: LandingProfileDockTone
  href?: string
  active?: boolean
  opportunityCount?: number
  onSelect?: () => void
}

type BuildLandingProfileDockItemsInput = {
  homeHref: string
  hasRoulette: boolean
  rouletteOpportunityCount?: number
  activeTab: LandingProfileDockTabId
  onTab: (id: LandingProfileDockTabId) => void
  onInfo: () => void
  onShop?: () => void
}

/** Giros prontos e giros que o comprador pode liberar compartilhando. */
export function getRouletteOpportunityCount(
  balance: LandingRouletteSpinsResponse | null
): number {
  if (!balance) return 0

  const spins = Math.max(0, balance.available_spins)
  const shareSpins =
    Math.max(0, balance.shares_available) * Math.max(0, balance.spins_per_share)
  const unlockAction =
    balance.roulette_unlocked === false && balance.paid_purchases > 0 ? 1 : 0

  return Math.max(spins + shareSpins, unlockAction)
}

export function scrollLandingProfileAnchor(id: string) {
  const node = document.getElementById(id)
  if (!node || typeof node.scrollIntoView !== 'function') return
  node.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

export function buildLandingProfileDockItems({
  homeHref,
  hasRoulette,
  rouletteOpportunityCount = 0,
  activeTab,
  onTab,
  onInfo,
  onShop,
}: BuildLandingProfileDockItemsInput): LandingProfileDockItem[] {
  const campanha: LandingProfileDockItem = {
    id: 'campanha',
    label: 'Campanha',
    accessibleName: 'Ir para a campanha',
    icon: 'mdi:home-variant',
    tone: 'home',
    href: homeHref,
  }
  const numeros: LandingProfileDockItem = {
    id: 'numeros',
    label: 'Números',
    accessibleName: 'Atalho para números',
    icon: 'mdi:ticket-confirmation',
    tone: 'numbers',
    active: activeTab === 'numeros',
    onSelect: () => onTab('numeros'),
  }
  const roleta: LandingProfileDockItem = {
    id: 'roleta',
    label: 'Roleta',
    accessibleName:
      rouletteOpportunityCount > 0
        ? `Atalho para roleta, ${rouletteOpportunityCount} ${rouletteOpportunityCount === 1 ? 'oportunidade disponível' : 'oportunidades disponíveis'}`
        : 'Atalho para roleta',
    icon: 'mdi:poker-chip',
    tone: 'roulette',
    active: activeTab === 'roleta',
    opportunityCount: rouletteOpportunityCount,
    onSelect: () => onTab('roleta'),
  }
  const info: LandingProfileDockItem = {
    id: 'info',
    label: 'Informações',
    accessibleName: 'Atalho para informações',
    icon: 'mdi:information',
    tone: 'info',
    onSelect: onInfo,
  }
  const cadastro: LandingProfileDockItem = {
    id: 'cadastro',
    label: 'Cadastro',
    accessibleName: 'Atalho para cadastro',
    icon: 'mdi:account',
    tone: 'profile',
    active: activeTab === 'cadastro',
    onSelect: () => onTab('cadastro'),
  }
  const items = hasRoulette
    ? [numeros, roleta, info, cadastro]
    : [campanha, numeros, info, cadastro]

  if (onShop) {
    items.push({
      id: 'shop',
      label: 'Loja',
      accessibleName: 'Abrir loja',
      icon: 'mdi:shopping-outline',
      tone: 'shop',
      onSelect: onShop,
    })
  }

  return items
}
